import type { MetafieldsNamespaceKeyTypeMapping } from "@v1/reducers/commerce-reducer";
import type { ProductResolutionDependencies } from "replo-runtime/store/ReploProduct";
import type { AlchemyActionTrigger } from "schemas/alchemyActionTrigger";
import type { Component } from "schemas/component";
import type { ShopifyMetafieldNamespacedKey } from "schemas/generated/shopifyMetafield";
import type { ReploState } from "schemas/generated/symbol";
import type {
  Dependencies,
  Dependency,
  ProductsDependency,
} from "schemas/runtimeDependencies";
import type {
  MetafieldEntityType,
  MetafieldKey,
  MetafieldNamespace,
  MetafieldType,
  StoreProduct,
} from "schemas/runtimeProduct";

import forEach from "lodash-es/forEach";
import keyBy from "lodash-es/keyBy";
import mapValues from "lodash-es/mapValues";
import uniqBy from "lodash-es/uniqBy";
import {
  calculateProductDependenciesForActionTriggers,
  calculateProductDependenciesFromProps,
  forEachComponentAndDescendants,
} from "replo-runtime/shared/utils/component";
import { getNonDesignLibraryDynamicDataExpressions } from "replo-runtime/shared/utils/dynamic-data";
import { actionTypeToRenderData } from "replo-runtime/store/utils/action";
import { fakeProductsMap } from "replo-runtime/store/utils/fakeProducts";
import { filterNulls } from "replo-utils/lib/array";
import { DefaultMap } from "replo-utils/lib/map";
import { AlchemyActionTriggers } from "schemas/alchemyActionTrigger";
import { mediaSizes } from "schemas/breakpoints";
import { bundleConfigSchema } from "schemas/bundleDiscounts";

/**
 * Given a component, execute the closure for every property that is a dynamic
 * value. The same value may be repeated more than once.
 *
 * Note (Noah, 2023-10-03, USE-413): This function also reports dynamic values
 * which are set in a Replo State (variant) override.
 *
 * @param component Component to iterate over
 * @param componentOverrides Mapping of component id -> any overrides which have
 * been applied by Replo States for the component or its parents (this generally
 * should come from forEachComponentAndDescendants)
 * @param execute Closure to execute for each dynamic value
 */
const forEachDynamicValueProperty = (
  component: Component,
  componentOverrides: Record<
    Component["id"],
    { variantId: ReploState["id"]; partialComponent: Partial<Component> }[]
  >,
  execute: (handlebarsPath: string) => void,
) => {
  // Note (Noah, 2023-10-03): We'll construct an array of all the props we need
  // to check. In the simple case this is just component props, but if there are
  // state overrides, then we'll need to check the props of those overrides as well
  const propsObjectsToCheck: Partial<Component["props"]>[] = [
    component.props ?? {},
  ];

  // Note (Noah, 2023-10-03): Go through all the state overrides and add their props
  // to the list of ones to check. This ensures that even if there are overrides
  // for a component in two variants, or at two levels of the component-state tree,
  // we'll check all possible prop values for dynamic data strings.
  const overrides = componentOverrides[component.id] ?? [];
  for (const override of overrides) {
    if (override.partialComponent.props) {
      propsObjectsToCheck.push(override.partialComponent.props ?? {});
    }
  }

  // Note (Noah, 2023-10-03): Now go through all of the props, and for each
  // set of props, go through all the values and check if they're dynamic data
  // (treating the styles as a special case, since we need to check the inner
  // objects of each media device override)
  for (const props of propsObjectsToCheck) {
    for (const [key, value] of Object.entries(props)) {
      if (typeof value === "string" && value.includes("{{")) {
        execute(value);
      }
      if (AlchemyActionTriggers.includes(key as AlchemyActionTrigger)) {
        const actions = component.props?.[key as AlchemyActionTrigger];
        (actions ?? []).forEach((action) => {
          const renderData = actionTypeToRenderData[action.type];
          if (renderData?.supportsDynamicData) {
            for (const dynamicDataValue of renderData.getDynamicDataValues({
              action,
            })) {
              execute(dynamicDataValue);
            }
          }
        });
      }
    }

    for (const styleAttribute of mediaSizes
      .map((size) => `style@${size}`)
      .concat("style")) {
      const styleValue = props?.[styleAttribute];
      if (styleValue && typeof styleValue === "object") {
        for (const value of Object.values(styleValue)) {
          if (typeof value === "string" && value.includes("{{")) {
            execute(value);
          }
        }
      }
    }
  }
};

/**
 * Given a component, return an array of all the things that component depends on.
 *
 * This is the main function which tells us what we have to fetch in the backend,
 * serialize to liquid, etc during the publish flow. In particular, this determines
 * all the products/metafields/data collections that we need.
 *
 * @param rootComponent Component to calculate dependencies for
 * @param extras Extra data needed to figure out dependencies.
 * @returns Mapping of component id -> dependencies for that component
 */
export function calculateDependencies(
  rootComponent: Component,
  extras: {
    productResolutionDependencies: ProductResolutionDependencies;
    metafieldsNamespaceKeyTypeMapping: MetafieldsNamespaceKeyTypeMapping;
    templateProductId: string | number | null;
  },
): {
  dependencies: Dependencies;
  allReferencedMetafields: Record<
    MetafieldEntityType,
    ShopifyMetafieldNamespacedKey[]
  >;
} {
  const result = new DefaultMap<string, Dependency[]>(() => []);

  const productMetafieldMap = new DefaultMap<
    string,
    ShopifyMetafieldNamespacedKey[]
  >(() => []);
  const variantMetafieldMap = new DefaultMap<
    string,
    ShopifyMetafieldNamespacedKey[]
  >(() => []);

  forEachComponentAndDescendants(
    rootComponent,
    (component, _, context) => {
      const productDependenciesFromProps =
        calculateProductDependenciesFromProps(component);
      if (productDependenciesFromProps?.length > 0) {
        result.get(component.id)!.push(...productDependenciesFromProps);
      }

      const productDependenciesFromActionTriggers =
        calculateProductDependenciesForActionTriggers(component);
      if (productDependenciesFromActionTriggers?.length > 0) {
        result
          .get(component.id)!
          .push(...productDependenciesFromActionTriggers);
      }

      if (component.type === "bundleBuilder") {
        const bundleId = component.id;
        const bundleConfig = {
          id: bundleId,
          isTiered: Boolean(component.props.isTiered),
          allowMultipleOfSameItem: Boolean(
            component.props.allowMultipleOfSameItem,
          ),
          targetType: component.props.bundleTargetType,
          productsWithVariants: component.props.productsWithVariants,
          thresholds: component.props.bundleDiscountThresholds,
        };
        const parsedConfig = bundleConfigSchema.parse(bundleConfig);
        result
          .get(component.id)!
          .push({ type: "bundleConfig", config: parsedConfig });
      }

      forEachDynamicValueProperty(
        component,
        // Note (Noah, 2023-12-04, REPL-9613): Important to note here for
        // performance, that componentOverrides could be quite a large objects
        // (potentially 1000s of nested objects). Weird to pass the whole thing
        // here, but if we did something like a mapValues, we'd be doing a lot
        // of work, and we'd be doing it for every single component in the tree,
        // which can slow down element updates a lot.
        context.componentOverrides ?? {},
        (propertyValue) => {
          // NOTE (Fran 2025-01-09): In the future we may want to support multiple
          // dynamic data expressions, but for now we only support one.
          const nonDesignLibraryDynamicDataExpressions =
            getNonDesignLibraryDynamicDataExpressions(propertyValue) ?? [];
          const propertyValueWithoutHtmlTags =
            nonDesignLibraryDynamicDataExpressions.length > 0
              ? nonDesignLibraryDynamicDataExpressions[0]!
              : propertyValue;

          if (propertyValue.includes(".productMetafields.")) {
            const namespaceAndKey = propertyValueWithoutHtmlTags
              .split(".productMetafields.")[1]!
              .replace("}}", "");

            const [namespace, key] = namespaceAndKey.split(".");

            if (!namespace || !key) {
              // eslint-disable-next-line replo/no-raw-error-throw
              throw new Error(`Invalid dynamic data path: ${propertyValue}`);
            }

            productMetafieldMap.get(component.id)!.push({
              namespace,
              key,
            });
          }
          if (propertyValue.includes(".variantMetafields.")) {
            const namespaceAndKey = propertyValueWithoutHtmlTags
              .split(".variantMetafields.")[1]!
              .replace("}}", "");

            const [namespace, key] = namespaceAndKey.split(".");
            if (!namespace || !key) {
              // eslint-disable-next-line replo/no-raw-error-throw
              throw new Error(`Invalid dynamic data path: ${propertyValue}`);
            }

            variantMetafieldMap.get(component.id)!.push({
              namespace,
              key,
            });
          }
        },
      );

      return "continue";
    },
    null,
    {},
    { executeForAllVariantOverrides: true },
  );

  const productsForDependencies = getProductsDataForDependencies(
    Object.fromEntries(result.entries()),
    keyBy(extras.productResolutionDependencies.products, (p) => p.id),
    extras.templateProductId,
  );
  for (const [
    componentId,
    referencedMetafields,
  ] of productMetafieldMap.entries()) {
    for (const product of productsForDependencies) {
      result.get(componentId)!.push({
        type: "metafields",
        productId: product.id.toString(),
        productHandle: product.handle,
        entityType: "product",
        metafieldKeys: uniqBy(
          referencedMetafields,
          ({ namespace, key }) => `${namespace}.${key}`,
        ).map(({ namespace, key }) => ({
          namespace,
          key,
          type:
            extras.metafieldsNamespaceKeyTypeMapping?.product?.[
              namespace as string
            ]?.[key as string] ?? "single_line_text_field",
        })),
      });
    }
  }

  for (const [
    componentId,
    referencedMetafields,
  ] of variantMetafieldMap.entries()) {
    for (const product of productsForDependencies) {
      const applicableVariants = product.variants.map((variant) => {
        return {
          variantId: variant.id,
          productHandle: product.handle,
          productId: product.id,
        };
      });

      for (const variant of applicableVariants) {
        result.get(componentId)!.push({
          type: "metafields",
          variantId: variant.variantId.toString(),
          productHandle: variant.productHandle,
          entityType: "variant",
          metafieldKeys: uniqBy(
            referencedMetafields,
            ({ namespace, key }) => `${namespace}.${key}`,
          ).map(({ namespace, key }) => ({
            namespace,
            key,
            type:
              extras.metafieldsNamespaceKeyTypeMapping?.variant?.[
                namespace as string
              ]?.[key as string] ?? "single_line_text_field",
          })),
        });
      }
    }
  }

  return {
    dependencies: Object.fromEntries(result.entries()),
    allReferencedMetafields: {
      product: uniqBy(
        Array.from(productMetafieldMap.values()).flatMap((v) => v),
        (object) => {
          return JSON.stringify(object);
        },
      ),
      variant: uniqBy(
        Array.from(variantMetafieldMap.values()).flatMap((v) => v),
        (object) => {
          return JSON.stringify(object);
        },
      ),
    },
  };
}

export const getProductsDataForDependencies = (
  dependencies: Dependencies,
  productDataFromStore: Record<number, StoreProduct>,
  templateProductId: string | number | null,
) => {
  const productDataFromStoreWithFakeProducts: Record<string, StoreProduct> = {
    ...productDataFromStore,
    ...fakeProductsMap,
  };
  const productsDependencies = Object.values(dependencies)
    .flat()
    .filter(
      (dependency) => dependency.type === "products",
    ) as ProductsDependency[];

  const productsIds = productsDependencies
    .reduce((productsIdArray: (string | number)[], d) => {
      return [...productsIdArray, ...d.productIds];
    }, [])
    .concat(templateProductId ? [templateProductId] : []);

  const uniqProductsIds = uniqBy(productsIds, Number);

  const productsData = filterNulls(
    uniqProductsIds.map(
      (id) => productDataFromStoreWithFakeProducts[String(id)],
    ),
  );

  return productsData;
};

export const getMetafieldsNamespaceKeyTypeMapping = (
  metafieldValues: Record<number, Record<MetafieldNamespace, any>>,
) => {
  const metafieldsNamespaceKeyTypeMapping: Record<
    MetafieldNamespace,
    Record<MetafieldKey, MetafieldType>
  > = {};

  forEach(metafieldValues, (metafieldValuesForVariant) => {
    forEach(metafieldValuesForVariant, (metafieldObj, namespace) => {
      metafieldsNamespaceKeyTypeMapping[namespace] = {
        ...metafieldsNamespaceKeyTypeMapping[namespace],
        ...mapValues(metafieldObj, (metafield) => metafield.type),
      };
    });
  });

  return metafieldsNamespaceKeyTypeMapping;
};
